/**
 * CatererServiceStylesCard — tickboxes for caterer vendors to declare
 * which serving styles they offer. Customers filter caterers by buffet or
 * traditional in the Instant Pricing side panel.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ForkKnife, CheckSquare, Square } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STYLES = [
  {
    key: 'traditional',
    label: 'Traditional Serving',
    sub: 'Banana leaf / Indian thali / table-served. Waiters bring food to seated guests.',
  },
  {
    key: 'buffet',
    label: 'Buffet',
    sub: 'Self-service line. Guests serve themselves at a long counter.',
  },
];

const CatererServiceStylesCard = () => {
  const [styles, setStyles] = useState([]);
  const [pureVeg, setPureVeg] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchStyles(); }, []);

  const fetchStyles = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/me/service-styles`, { withCredentials: true });
      setStyles(data.styles || []);
      setPureVeg(!!data.pure_veg);
    } catch {
      // Not a caterer vendor — silently no-op.
    }
  };

  const toggle = async (key) => {
    const next = styles.includes(key) ? styles.filter((s) => s !== key) : [...styles, key];
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/me/service-styles`,
        { styles: next, pure_veg: pureVeg }, { withCredentials: true });
      setStyles(data.styles || []);
      setPureVeg(!!data.pure_veg);
      toast.success('Serving style updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const togglePureVeg = async () => {
    const next = !pureVeg;
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/me/service-styles`,
        { styles, pure_veg: next }, { withCredentials: true });
      setStyles(data.styles || []);
      setPureVeg(!!data.pure_veg);
      toast.success(next ? 'Marked as Pure Veg caterer' : 'Pure Veg unmarked');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <Card className="bg-white border-[#E8E1D5] mt-6" data-testid="caterer-service-styles-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ForkKnife size={20} className="text-brand-primary" /> Serving Styles You Offer
        </CardTitle>
        <CardDescription>
          Tick the styles you can serve. Customers filter caterers by these on the Instant Pricing screen, so
          ticking both gets you in front of every customer regardless of their preference.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {STYLES.map((s) => {
            const on = styles.includes(s.key);
            return (
              <button key={s.key} type="button"
                onClick={() => toggle(s.key)} disabled={saving}
                className={`w-full text-left flex items-start gap-3 p-3 border-2 rounded transition-colors ${
                  on ? 'border-brand-primary bg-brand-primary/5' : 'border-[#E8E1D5] hover:border-brand-primary/30'
                }`}
                data-testid={`caterer-style-toggle-${s.key}`}>
                {on
                  ? <CheckSquare size={22} weight="fill" className="text-brand-primary shrink-0 mt-0.5" />
                  : <Square size={22} className="text-text-secondary shrink-0 mt-0.5" />}
                <div>
                  <div className="text-sm font-bold text-text-primary">{s.label}</div>
                  <div className="text-xs text-text-secondary mt-0.5">{s.sub}</div>
                </div>
              </button>
            );
          })}
        </div>
        {styles.length === 0 && (
          <p className="text-[11px] text-amber-700 mt-3 italic">
            Tick at least one — caterers without any serving style picked won't appear in customer search results.
          </p>
        )}

        {/* Pure Veg flag — surfaces in customer's Brahmin-wedding filter. */}
        <div className="mt-4 pt-4 border-t border-[#E8E1D5]">
          <button type="button"
            onClick={togglePureVeg} disabled={saving}
            className={`w-full text-left flex items-start gap-3 p-3 border-2 rounded transition-colors ${
              pureVeg ? 'border-green-600 bg-green-50' : 'border-[#E8E1D5] hover:border-green-600/30'
            }`}
            data-testid="caterer-pure-veg-toggle">
            {pureVeg
              ? <CheckSquare size={22} weight="fill" className="text-green-700 shrink-0 mt-0.5" />
              : <Square size={22} className="text-text-secondary shrink-0 mt-0.5" />}
            <div>
              <div className="text-sm font-bold text-text-primary flex items-center gap-1.5">
                Pure Veg Caterer
                <span className="inline-block w-3 h-3 bg-green-600 rounded-sm border border-green-700" />
              </div>
              <div className="text-xs text-text-secondary mt-0.5">
                I serve ONLY pure-vegetarian food (no eggs, no onion/garlic on request). Brahmin-wedding customers filter
                specifically for this — ticking it puts you in front of them.
              </div>
            </div>
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CatererServiceStylesCard;
